/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

import * as go from 'gojs';

import { SKELETON_STYLES } from '../graph-config';
import { GoMake } from './shared';

/**
 * Skeleton/ghost placeholder node shown while turn children are loading.
 * Mimics the real agent/tool node structure: a circle with a label bar below.
 */
export function createSkeletonNodeTemplate($: GoMake): go.Node {
  return $(
    go.Node,
    'Vertical',
    {
      selectionAdorned: false,
      layoutConditions: go.LayoutConditions.Added | go.LayoutConditions.Removed,
      pickable: false, // Non-interactive
    },
    // Ghost circle (same size as real agent/tool: 48x48)
    $(
      go.Panel,
      'Spot',
      $(go.Shape, 'Circle', {
        name: 'SKELETON_SHAPE',
        desiredSize: SKELETON_STYLES.circle.size,
        fill: SKELETON_STYLES.circle.fill,
        stroke: SKELETON_STYLES.circle.stroke,
        strokeWidth: SKELETON_STYLES.circle.strokeWidth,
      }),
      // Placeholder inner shape (smaller circle to hint at an icon)
      $(go.Shape, 'Circle', {
        desiredSize: SKELETON_STYLES.innerCircle.size,
        fill: SKELETON_STYLES.innerCircle.fill,
        stroke: SKELETON_STYLES.innerCircle.stroke,
        strokeWidth: SKELETON_STYLES.innerCircle.strokeWidth,
      }),
      // Left port
      $(go.Shape, 'Circle', {
        width: 2, height: 2,
        fill: 'transparent', stroke: 'transparent',
        portId: 'left',
        alignment: go.Spot.Left,
        fromLinkable: true, toLinkable: true,
      }),
      // Right port
      $(go.Shape, 'Circle', {
        width: 2, height: 2,
        fill: 'transparent', stroke: 'transparent',
        portId: 'right',
        alignment: go.Spot.Right,
        fromLinkable: true, toLinkable: true,
      })
    ),
    // Label bar below the circle (sized to match typical agent/tool name width)
    $(go.Shape, 'RoundedRectangle', {
      desiredSize: SKELETON_STYLES.labelBar.size,
      fill: SKELETON_STYLES.labelBar.fill,
      stroke: SKELETON_STYLES.labelBar.stroke,
      margin: SKELETON_STYLES.labelBar.margin,
      parameter1: SKELETON_STYLES.labelBar.cornerRadius,
    })
  );
}

/**
 * Start pulsing animation on all skeleton nodes in the diagram.
 * Call this after skeleton nodes have been added to the model.
 */
export function startSkeletonAnimation(diagram: go.Diagram): void {
  // Stop any existing skeleton animation first
  stopSkeletonAnimation(diagram);

  const animation = new go.Animation();
  animation.easing = go.Animation.EaseInOutQuad;
  animation.duration = 900;
  animation.reversible = true;
  animation.runCount = Infinity;

  let hasTargets = false;
  diagram.nodes.each((node) => {
    if (node.data?.category === 'skeleton') {
      const shape = node.findObject('SKELETON_SHAPE');
      if (shape) {
        animation.add(shape, 'opacity', 1.0, 0.3);
        hasTargets = true;
      }
    }
  });

  if (hasTargets) {
    // Store reference for cleanup
    (diagram as any).__skeletonAnimation = animation;
    animation.start();
  }
}

/**
 * Stop and clean up any running skeleton animation.
 */
export function stopSkeletonAnimation(diagram: go.Diagram): void {
  const existing = (diagram as any).__skeletonAnimation as go.Animation | undefined;
  if (existing) {
    existing.stop();
    delete (diagram as any).__skeletonAnimation;
  }
}
